import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FuseNavigationItem } from '../../../@fuse/components/navigation';
import { UserService } from '../user/user.service';

interface ISettingChild {
	path: string;
	permission: string;
}

@Injectable({
	providedIn: 'root',
})
export class NavigationService {
	private _navigation: ReplaySubject<Navigation> =
		new ReplaySubject<Navigation>(1);

	private readonly settingChildren: ISettingChild[] = [
		{
			path: 'settings/general',
			permission: 'setting:view',
		},
		{
			path: 'settings/fields',
			permission: 'setting:viewfields',
		},
		{
			path: 'settings/automation',
			permission: 'setting:viewautomation',
		},
		{
			path: 'settings/invoicing',
			permission: 'setting:viewinvoicing',
		},
		{
			path: 'settings/portal-admin',
			permission: 'setting:viewexternalportal',
		},
		{
			path: 'settings/users',
			permission: 'setting:viewusers',
		},
		{
			path: 'settings/document-templates',
			permission: 'setting:viewdoctemp',
		},
		{
			path: 'settings/support',
			permission: 'setting:support',
		},
		{
			path: 'settings/roles',
			permission: 'setting:viewroles',
		},
		{
			path: 'settings/plan-billing',
			permission: 'setting:billing',
		},
	];

	private readonly settingPermissions: string[] = [
		'setting:viewgeneral',
		'setting:viewfields',
		'setting:viewusers',
		'setting:viewdoctemp',
		'setting:support',
		'setting:viewroles',
		'setting:billing',
	];

	/**
	 * Constructor
	 */
	constructor(private _userService: UserService) {}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Getter for navigation
	 */
	get navigation$(): Observable<Navigation> {
		return this._navigation.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Get all navigation data
	 */
	get(): Observable<Navigation> {
		let navData = this._userService.getHasPermissionData(defaultNavigation);
		const settingsItem = navData.find((x) => x.id === 'settings');

		if (settingsItem) {
			const firstSettingPermission = this._userService
				.getPermissions()
				.find((x) => this.settingPermissions.includes(x.toLowerCase()));

			settingsItem.children = [
				{
					id: 'general',
					title: 'General',
					type: 'basic',
					icon: 'heroicons_outline:cog-6-tooth',
					link:
						this.settingChildren.find(
							(x) => x.permission === firstSettingPermission
						)?.path ?? '/settings/general',
				},
			];
		}

		let nav = {
			compact: navData,
			default: navData,
			futuristic: navData,
			horizontal: navData,
		};

		this._navigation.next(nav);

		return of(nav);
	}
}

export const defaultNavigation: FuseNavigationItem[] = [
	{
		id: 'dashboards',
		title: 'Dashboard',
		type: 'basic',
		link: '/dashboard',
		icon: 'heroicons_outline:home',
		meta: {
			permission: ['dashboard:view'],
		},
	},

	{
		id: 'mosaicview',
		title: 'Mosaic View',
		type: 'basic',
		icon: 'heroicons_outline:puzzle-piece',
		link: '/mosaic-view',
		meta: {
			permission: ['mosaicview:view'],
		},
	},

	{
		id: 'claims',
		title: 'Claims',
		type: 'basic',
		link: '/claims',
		icon: 'heroicons_outline:briefcase',
		meta: {
			permission: ['claim:allaccess', 'claim:view'],
		},
	},

	{
		id: 'leads',
		title: 'Leads',
		type: 'basic',
		icon: 'heroicons_outline:inbox-arrow-down',
		link: '/leads',
		meta: {
			permission: ['lead:allaccess', 'lead:view'],
		},
	},

	{
		id: 'contacts',
		title: 'Contacts',
		type: 'basic',
		icon: 'heroicons_outline:user-group',
		link: '/contacts',
		meta: {
			permission: ['contact:view'],
		},
	},

	{
		id: 'companies',
		title: 'Companies',
		type: 'basic',
		icon: 'heroicons_outline:building-office',
		link: '/companies',
		meta: {
			permission: ['company:view'],
		},
	},

	{
		id: 'tasks',
		title: 'Tasks',
		type: 'basic',
		icon: 'heroicons_outline:check-circle',
		link: '/tasks',
		meta: {
			permission: ['task:view'],
		},
	},

	{
		id: 'reports',
		title: 'Reports',
		type: 'basic',
		icon: 'heroicons_outline:document',
		link: '/reports',
		meta: {
			permission: ['report:view'],
		},
	},

	{
		id: 'agent',
		title: 'Agent',
		type: 'basic',
		icon: 'robot',
		link: '/agent',
		meta: {
			permission: ['admin:global'],
		},
	},

	{
		id: 'settings',
		title: 'SETTINGS',
		type: 'group',
		icon: 'heroicons_outline:cog-6-tooth',
		children: [],
		meta: {
			permission: ['setting:view'],
		},
	},
];

export const compactNavigation: FuseNavigationItem[] = [
	{
		id: 'dashboards',
		title: 'Dashboards',
		tooltip: 'Dashboards',
		type: 'aside',
		icon: 'heroicons_outline:home',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'apps',
		title: 'Apps',
		tooltip: 'Apps',
		type: 'aside',
		icon: 'heroicons_outline:qrcode',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'pages',
		title: 'Pages',
		tooltip: 'Pages',
		type: 'aside',
		icon: 'heroicons_outline:document-duplicate',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'user-interface',
		title: 'UI',
		tooltip: 'UI',
		type: 'aside',
		icon: 'heroicons_outline:collection',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'navigation-features',
		title: 'Navigation',
		tooltip: 'Navigation',
		type: 'aside',
		icon: 'heroicons_outline:bars-3',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
];
export const futuristicNavigation: FuseNavigationItem[] = [
	{
		id: 'dashboards',
		title: 'DASHBOARDS',
		type: 'group',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'apps',
		title: 'APPS',
		type: 'group',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'others',
		title: 'OTHERS',
		type: 'group',
	},
	{
		id: 'pages',
		title: 'Pages',
		type: 'aside',
		icon: 'heroicons_outline:document-duplicate',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'user-interface',
		title: 'User Interface',
		type: 'aside',
		icon: 'heroicons_outline:collection',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
	{
		id: 'navigation-features',
		title: 'Navigation Features',
		type: 'aside',
		icon: 'heroicons_outline:bars-3',
		children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
	},
];
export const horizontalNavigation: FuseNavigationItem[] = [
	{
		id: 'home',
		title: 'Home',
		type: 'basic',
		link: '/dashboard',
		icon: 'heroicons_outline:home',
		children: [],
	},
	{
		id: 'solicitations',
		title: 'Solicitations',
		type: 'basic',
		link: '/solicitations',
		icon: 'heroicons_outline:currency-dollar',
		children: [],
	},
	{
		id: 'contracts',
		title: 'Contract Orders',
		type: 'basic',
		link: '/contracts',
		icon: 'heroicons_outline:document-text',
		children: [],
	},
	{
		id: 'capture-trips',
		title: 'Capture Trips',
		type: 'basic',
		link: '/capture-trips',
		icon: 'heroicons_outline:video-camera',
		children: [],
	},
	{
		id: 'modules',
		title: 'Modules',
		type: 'basic',
		link: '/modules',
		icon: 'heroicons_outline:collection',
		children: [],
	},
	{
		id: 'admin',
		title: 'Admin',
		type: 'group',
		icon: 'heroicons_outline:cog',
		children: [
			{
				id: 'contacts',
				title: 'Contacts',
				type: 'basic',
				link: '/admin/contacts',
				icon: 'heroicons_outline:user-group',
				children: [],
			},
			{
				id: 'roles',
				title: 'Roles',
				type: 'basic',
				link: '/admin/roles',
				icon: 'heroicons_outline:shield-check',
				children: [],
			},
		],
	},
];
